import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useDeals, useStakeDebit, useUnclaimedTransactions, useUserAccount } from "../../CustomHooks/Queries";
import { useUpdateAddress } from "../../CustomHooks/Mutations";
import {
  BATTLECARD_PLAN_SELECTION_NAV_STATE,
  dispatchErrorToast,
  dispatchSuccessToast,
  formatCurrency,
  getUnclaimedCashBackAmount,
  getUnclaimedCashBackAmountNumeric,
  canChangeLegacyCheckPlanFromDealsResponse,
  getCheckIdFromDealsResponse,
  isLegacyMoveInCashPlanFromDealsResponse,
} from "../../Utils/helpers";
import { APIQueryKeys } from "../../Utils/constants";
import {
  addressFromCheckStatus,
  addressFromUser,
  buildUpdateAddressPayload,
  formatCheckStatusAddress,
  formatMailingDisplay,
  isAddressComplete,
  trimAddressFields,
} from "./check-address-utils";
import CheckMailingAddressEditForm, { EditIcon } from "./check-mailing-address-edit-form";
import LegacyCheckPageLayout from "./legacy-check-page-layout";
import "./battle-cards.scss";

const STEP_LABELS = [
  { label: "Property Approved", sublabel: "4–5 months" },
  { label: "Check Mailed", sublabel: "1–2 weeks" },
  { label: "Delivered", sublabel: null },
];

const CHECK_STATUS_STEP_INDEX = {
  property_approved: 0,
  check_mailed: 1,
  delivered: 2,
};

const TRACK_FILL_BY_STEP = ["0%", "50%", "100%"];

export default function CheckOnTheWay() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: dealsData } = useDeals();
  const { data: stakeDebitData } = useStakeDebit();
  const { data: userAccountData } = useUserAccount();
  const { data: unclaimedTransactionsData } = useUnclaimedTransactions();

  const user = userAccountData?.data?.data?.user;

  const checkStatus = dealsData?.data?.data?.currentDeal?.check_status;
  const checkStatusValue = checkStatus?.status;
  const currentStepIndex = CHECK_STATUS_STEP_INDEX[checkStatusValue] ?? 0;
  const canChangePlan = canChangeLegacyCheckPlanFromDealsResponse(dealsData, stakeDebitData);

  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [draft, setDraft] = useState(() => addressFromCheckStatus(checkStatus?.address, user));

  const activeAddress = useMemo(
    () => addressFromCheckStatus(checkStatus?.address, user),
    [checkStatus?.address, user]
  );

  const { mutate: updateAddress, isLoading: isSavingAddress } = useUpdateAddress({
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries(APIQueryKeys.deals),
        queryClient.refetchQueries(APIQueryKeys.userAccount),
      ]);
      dispatchSuccessToast("Your mailing address has been updated");
      setIsEditingAddress(false);
    },
    onError: (err) => {
      dispatchErrorToast(
        err?.response?.data?.error?.msg ??
          err?.response?.data?.msg ??
          "Unable to update your mailing address. Please try again."
      );
    },
  });

  useEffect(() => {
    if (
      isLegacyMoveInCashPlanFromDealsResponse(dealsData) &&
      checkStatusValue?.toLowerCase() === "delivered"
    ) {
      navigate("/home", { replace: true });
    }
  }, [checkStatusValue, dealsData, navigate]);

  useEffect(() => {
    if (!canChangePlan && isEditingAddress) {
      setIsEditingAddress(false);
    }
  }, [canChangePlan, isEditingAddress]);

  useEffect(() => {
    if (!isEditingAddress) {
      setDraft(activeAddress);
    }
  }, [activeAddress, isEditingAddress]);

  const steps = useMemo(
    () =>
      STEP_LABELS.map((step, index) => ({
        ...step,
        dotActive: index <= currentStepIndex,
        labelActive: index === currentStepIndex,
      })),
    [currentStepIndex]
  );

  const trackFillWidth = TRACK_FILL_BY_STEP[currentStepIndex] ?? "0%";

  const mailingAddress = useMemo(() => {
    const fromCheckStatus = formatCheckStatusAddress(checkStatus?.address);
    const fromUser = formatMailingDisplay(addressFromUser(user));

    return {
      name: fromCheckStatus.name || fromUser.name,
      street: fromCheckStatus.street || fromUser.street,
      cityLine: fromCheckStatus.cityLine || fromUser.cityLine,
    };
  }, [checkStatus?.address, user]);

  const checkAmountFromRequest = location.state?.checkAmount;
  const checkAmountFromDeals = checkStatus?.amount;

  const checkAmountNumeric = useMemo(() => {
    const amount = checkAmountFromDeals ?? checkAmountFromRequest;
    if (amount != null && amount !== "") {
      const parsed = Number(amount);
      return Number.isFinite(parsed) ? parsed : getUnclaimedCashBackAmountNumeric(unclaimedTransactionsData);
    }

    return getUnclaimedCashBackAmountNumeric(unclaimedTransactionsData);
  }, [checkAmountFromDeals, checkAmountFromRequest, unclaimedTransactionsData]);

  const cashBackAmount = useMemo(() => {
    const amount = checkAmountFromDeals ?? checkAmountFromRequest;
    if (amount != null && amount !== "") {
      const parsed = Number(amount);
      return Number.isFinite(parsed) ? formatCurrency(parsed) : amount;
    }

    return getUnclaimedCashBackAmount(unclaimedTransactionsData);
  }, [checkAmountFromDeals, checkAmountFromRequest, unclaimedTransactionsData]);

  function goToPlans() {
    navigate("/battlecard", { state: BATTLECARD_PLAN_SELECTION_NAV_STATE });
  }

  function startEditingAddress() {
    setDraft({ ...activeAddress });
    setIsEditingAddress(true);
  }

  function cancelEditingAddress() {
    setDraft({ ...activeAddress });
    setIsEditingAddress(false);
  }

  function updateDraftField(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  function saveAddress() {
    const trimmed = trimAddressFields(draft);

    if (!isAddressComplete(trimmed)) {
      dispatchErrorToast("Please fill in all required address fields.");
      return;
    }

    updateAddress(
      buildUpdateAddressPayload(trimmed, {
        withdrawalId: getCheckIdFromDealsResponse(dealsData),
      }),
    );
  }

  return (
    <LegacyCheckPageLayout>
      <div
        className={`battle-cards-page bc-check-on-the-way-page${
          isEditingAddress ? " is-editing-address" : ""
        }`}
      >
        <div className="bc-check-on-the-way-content">
          {isEditingAddress ? (
            <CheckMailingAddressEditForm
              draft={draft}
              onFieldChange={updateDraftField}
              onSave={saveAddress}
              onCancel={cancelEditingAddress}
              isSaving={isSavingAddress}
            />
          ) : (
            <>
          <div className="bc-check-status-card">
            <h2 className="bc-check-status-title">Your check is on the way</h2>

            <div className="bc-check-stepper">
              <div className="bc-check-stepper-track-row">
                <div className="bc-check-stepper-track-bg" />
                <div className="bc-check-stepper-track-fill" style={{ width: trackFillWidth }} />
                <div className="bc-check-stepper-dots">
                  {steps.map((step) => (
                    <span
                      key={step.label}
                      className={`bc-check-stepper-dot${step.dotActive ? " active" : ""}`}
                    />
                  ))}
                </div>
              </div>
              <div className="bc-check-stepper-labels">
                {steps.map((step, i) => (
                  <div
                    key={step.label}
                    className={`bc-check-stepper-label-col${
                      i === 1 ? " center" : i === 2 ? " end" : ""
                    }`}
                  >
                    <span className={`bc-check-stepper-label${step.labelActive ? " active" : ""}`}>
                      {step.label}
                    </span>
                    {step.sublabel && (
                      <span className="bc-check-stepper-sublabel">{step.sublabel}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bc-check-status-divider" />

            <div className="bc-check-status-row">
              <div className="bc-check-status-col">
                <span className="bc-check-status-label">Current Status</span>
                <span className="bc-check-status-value">
                  {checkStatus?.current_text}
                </span>
              </div>
              <div className="bc-check-status-col end">
                <span className="bc-check-status-label">Estimated Delivery</span>
                <span className="bc-check-status-value">
                  {checkStatus?.estimated_delivery}
                </span>
              </div>
            </div>

            <div className="bc-check-on-the-way-address-row">
              <div className="bc-check-mailing-address bc-check-on-the-way-mailing">
                <span className="bc-check-mailing-label">Mailing Address</span>
                {canChangePlan && (
                  <button
                    type="button"
                    className="bc-check-mailing-edit"
                    aria-label="Edit mailing address"
                    onClick={startEditingAddress}
                  >
                    <EditIcon />
                  </button>
                )}
                {mailingAddress.name && (
                  <p className="bc-check-mailing-line name">{mailingAddress.name}</p>
                )}
                {mailingAddress.street && (
                  <p className="bc-check-mailing-line">{mailingAddress.street}</p>
                )}
                {mailingAddress.cityLine && (
                  <p className="bc-check-mailing-line">{mailingAddress.cityLine}</p>
                )}
              </div>
            </div>
          </div>

          {canChangePlan && (
            <div className="bc-check-upgrade-banner">
              <div className="bc-check-upgrade-accent" />
              <div className="bc-check-upgrade-body">
                <div className="bc-check-upgrade-copy">
                  <h3 className="bc-check-upgrade-title">{`Why wait months? Get your ${cashBackAmount} now.`}</h3>
                  <p className="bc-check-upgrade-desc">
                    Upgrade to Stake Checking for instant cashback, plus $500+ in yearly savings and ongoing rewards.
                  </p>
                </div>
                <button type="button" className="bc-check-upgrade-btn" onClick={goToPlans}>
                  Choose a Plan
                </button>
              </div>
            </div>
          )}
            </>
          )}
        </div>
      </div>
    </LegacyCheckPageLayout>
  );
}
